import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  LECTURER_ROLES,
  LECTURER_ROLE_LABEL,
  formatWeekdays,
  type LecturerRole,
} from '@doit/shared';
import { ArrowLeft, Pencil, Plus, Trash2, UserMinus } from 'lucide-react';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Button, Card, EmptyState, FullPageLoader, PageHeader } from '../../components/ui';
import { Select } from '../../components/form';
import { useToast } from '../../components/Toast';
import {
  DirectionTag,
  FormatBadge,
  GroupStatusBadge,
  ProjectBadge,
  StageBadge,
} from '../../components/badges';
import { ApiError } from '../../api/client';
import { useCurrentUser } from '../../api/auth';
import {
  useAssignLecturer,
  useDeleteGroup,
  useGroup,
  useLecturers,
  useRemoveLecturer,
} from '../../api/queries';
import { formatDate } from '../../lib/format';
import { GroupModal } from './GroupModal';
import { GroupStudents } from './GroupStudents';
import { GroupSchedule } from './GroupSchedule';

export function GroupDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const user = useCurrentUser();
  const isStaff = user.role !== 'LECTURER';

  const { data: group, isLoading, error } = useGroup(id);
  const removeGroup = useDeleteGroup();

  const [editOpen, setEditOpen] = useState(false);
  const [lecturerOpen, setLecturerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toUnassign, setToUnassign] = useState<string | null>(null);

  const unassign = useRemoveLecturer(id);

  if (isLoading) return <FullPageLoader />;

  if (error || !group) {
    return (
      <div className="rounded-[10px] border border-line bg-surface shadow-xs">
        <EmptyState
          title="ჯგუფი მიუწვდომელია"
          description={error instanceof ApiError ? error.message : 'ჯგუფი ვერ მოიძებნა'}
          action={
            <Link to="/groups">
              <Button variant="secondary">ჯგუფების სიაში დაბრუნება</Button>
            </Link>
          }
        />
      </div>
    );
  }

  async function handleDelete() {
    try {
      await removeGroup.mutateAsync(id);
      toast.success('ჯგუფი წაიშალა');
      navigate('/groups');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'წაშლა ვერ მოხერხდა');
      setConfirmDelete(false);
    }
  }

  async function handleUnassign(userId: string) {
    try {
      await unassign.mutateAsync(userId);
      toast.success('ლექტორი მოიხსნა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ მოიხსნა');
    } finally {
      setToUnassign(null);
    }
  }

  const progress =
    group.totalLessons > 0 ? Math.round((group.heldCount / group.totalLessons) * 100) : 0;

  return (
    <>
      <Link
        to="/groups"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-ink-mute transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        ჯგუფები
      </Link>

      <PageHeader
        title={group.name}
        subtitle={group.course.name}
        action={
          isStaff ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                icon={<Pencil className="size-4" />}
                onClick={() => setEditOpen(true)}
              >
                რედაქტირება
              </Button>
              <Button
                variant="ghost"
                icon={<Trash2 className="size-4" />}
                onClick={() => setConfirmDelete(true)}
              >
                წაშლა
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <GroupStatusBadge status={group.status} />
        <FormatBadge format={group.format} />
        <ProjectBadge project={group.flow.project} />
        <StageBadge stage={group.currentStage} stageCount={group.course.stageCount} />
        <DirectionTag name={group.course.direction.name} color={group.course.direction.color} />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* --- ინფორმაცია --- */}
        <Card title="ინფორმაცია" className={isStaff ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <dl className="grid gap-x-8 gap-y-0 sm:grid-cols-2">
            <Row label="ნაკადი" value={group.flow.name} />
            <Row label="კურსი" value={`${group.course.name} (${group.course.code})`} />
            <Row
              label="გაკვეთილის დღეები"
              value={group.weekdays.length ? formatWeekdays(group.weekdays) : '—'}
            />
            <Row label="დრო" value={`${group.startTime} · ${group.durationMin} წთ`} />
            <Row label="დაწყება" value={formatDate(group.startDate)} />
            <Row label="დასრულება" value={formatDate(group.endDate)} />
            <Row label="აუდიტორია" value={group.room ?? '—'} />
            <Row label="სტუდენტი" value={String(group._count.enrollments)} />
          </dl>

        </Card>

        {/* --- პროგრესი: მენეჯერის ხედი --- */}
        {isStaff && (
        <Card title="გაკვეთილები">
          <div className="text-center">
            <p className="tabular text-3xl font-semibold text-ink">
              {group.heldCount}
              <span className="text-lg text-ink-mute"> / {group.totalLessons || '?'}</span>
            </p>
            <p className="mt-1 text-[13px] text-ink-mute">ჩატარებული</p>
          </div>

          {group.totalLessons > 0 && (
            <div className="mt-4">
              <div className="h-2 overflow-hidden rounded-full bg-canvas">
                <div
                  className="h-full rounded-full bg-brand-600 transition-all"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <p className="tabular mt-1.5 text-right text-[12px] text-ink-mute">{progress}%</p>
            </div>
          )}

          {!group.started && (
            <p className="mt-4 rounded-lg bg-warn-soft px-3 py-2 text-[12px] text-warn">
              ჯგუფს ჯერ არცერთი გაკვეთილი არ ჩატარებია.
            </p>
          )}

        </Card>
        )}

        {/* --- ლექტორები: მენეჯერი მართავს, ლექტორს არ სჭირდება --- */}
        {isStaff && (
        <Card
          title="ლექტორები"
          className="lg:col-span-3"
          action={
            isStaff ? (
              <Button
                size="sm"
                variant="secondary"
                icon={<Plus className="size-4" />}
                onClick={() => setLecturerOpen(true)}
              >
                მიმაგრება
              </Button>
            ) : undefined
          }
        >
          {group.lecturers.length === 0 ? (
            <EmptyState
              title="ლექტორი მიმაგრებული არ არის"
              description="ლექტორი ჯგუფს რომ დაინახოს, ჯერ უნდა მიემაგროს."
            />
          ) : (
            <ul className="divide-y divide-line">
              {group.lecturers.map((l) => (
                <li key={l.user.id} className="flex items-center gap-3 py-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-50 text-[12px] font-semibold text-brand-700">
                    {l.user.initials ?? l.user.fullName.slice(0, 2)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-ink">
                      {l.user.fullName}
                    </span>
                    <span className="block truncate text-[12px] text-ink-mute">{l.user.email}</span>
                  </span>
                  <span className="text-[12px] text-ink-mute">
                    {LECTURER_ROLE_LABEL[l.role as LecturerRole]}
                  </span>
                  {isStaff && (
                    <button
                      onClick={() => setToUnassign(l.user.id)}
                      aria-label="მოხსნა"
                      className="rounded-md p-1.5 text-ink-mute transition hover:bg-danger-soft hover:text-danger"
                    >
                      <UserMinus className="size-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
        )}
      </div>

      <div className="mt-5 space-y-5">
        <GroupStudents groupId={id} canEdit={isStaff} />
        {/* გაკვეთილების სია მენეჯერის ხედია — ლექტორი განრიგსა და აღრიცხვაში მუშაობს */}
        {isStaff && <GroupSchedule group={group} canEdit={isStaff} />}
      </div>

      <GroupModal open={editOpen} onClose={() => setEditOpen(false)} group={group} />

      <AssignLecturerModal
        open={lecturerOpen}
        onClose={() => setLecturerOpen(false)}
        groupId={id}
        assignedIds={group.lecturers.map((l) => l.user.id)}
      />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => void handleDelete()}
        title="ჯგუფის წაშლა"
        message={
          <>
            წაიშლება <strong>{group.name}</strong>. თუ ჯგუფში სტუდენტები ან გაკვეთილებია, წაშლა
            დაიბლოკება — ამ შემთხვევაში სტატუსი შეცვალე „გაუქმებულით".
          </>
        }
        confirmLabel="წაშლა"
        danger
        busy={removeGroup.isPending}
      />

      <ConfirmDialog
        open={Boolean(toUnassign)}
        onClose={() => setToUnassign(null)}
        onConfirm={() => toUnassign && void handleUnassign(toUnassign)}
        title="ლექტორის მოხსნა"
        message="ლექტორი ამ ჯგუფს ვეღარ დაინახავს. დასწრების ისტორია რჩება."
        confirmLabel="მოხსნა"
        danger
        busy={unassign.isPending}
      />
    </>
  );
}

// ============================================================

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-2.5 text-[13px] last:border-0">
      <dt className="text-ink-mute">{label}</dt>
      <dd className="text-right font-medium text-ink">{value}</dd>
    </div>
  );
}

function AssignLecturerModal({
  open,
  onClose,
  groupId,
  assignedIds,
}: {
  open: boolean;
  onClose: () => void;
  groupId: string;
  assignedIds: string[];
}) {
  const toast = useToast();
  const { data: lecturers } = useLecturers();
  const assign = useAssignLecturer(groupId);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<string>('PRIMARY');

  const available = (lecturers ?? []).filter((l) => !assignedIds.includes(l.id));

  async function submit() {
    if (!userId) {
      toast.error('აირჩიე ლექტორი');
      return;
    }
    try {
      await assign.mutateAsync({ userId, role });
      toast.success('ლექტორი მიემაგრა');
      setUserId('');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'მიმაგრება ვერ მოხერხდა');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="ლექტორის მიმაგრება"
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={assign.isPending}>
            გაუქმება
          </Button>
          <Button size="sm" onClick={() => void submit()} loading={assign.isPending}>
            მიმაგრება
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label="ლექტორი"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder={available.length ? 'აირჩიე ლექტორი' : 'ხელმისაწვდომი ლექტორი არ არის'}
          options={available.map((l) => ({
            value: l.id,
            label: `${l.fullName}${l.status === 'INVITED' ? ' (მოწვეული)' : ''}`,
          }))}
        />
        <Select
          label="როლი ჯგუფში"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          options={LECTURER_ROLES.map((r) => ({ value: r, label: LECTURER_ROLE_LABEL[r] }))}
        />
      </div>
    </Modal>
  );
}
